import React, { useState } from 'react';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Divider,
  Paper,
  Tooltip,
  IconButton,
  CircularProgress,
  Chip
} from '@material-ui/core';
import {
  AuxInfo,
  BadgeSection,
  PriceSection,
  ViewContainersModal
} from 'components/underwrite/shared/header';
import { HeaderImage } from 'components/underwrite/postAcquisition/postAcqUnderwrite/header';
import { fullAddress } from 'utilities/address';
import { toCurrency, asDate } from 'utilities/format';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  setPostAcqWorkflowGridStateUpdate, fetchPostAcqUWMsrHomes, setIsFetchingPostAcqUWMsrHomes,
  setPostAcqCmaDialogIsOpen, fetchPostAcqCma
} from 'store/actions';
import { push } from 'connected-react-router';
import { Constants } from 'utilities/constants';
import HouseIcon from '@material-ui/icons/House';
import { MsrHomesModal } from 'components/underwrite/shared';
import AmherstIcon from "assets/images/rex/AmherstIcon.png";
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';
import WaveIcon from '@material-ui/icons/Waves';
import AssessmentIcon from '@material-ui/icons/Assessment';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PostAcqCmaDialog from 'components/underwrite/postAcquisition/postAcqUnderwrite/PostAcqCmaDialog';
import { postAcquisitionTaxonomySelector, userRoleUnderwriteManagerSelector } from 'store/selectors';
import { isAcquisitionsChannel } from 'components/underwrite/postAcquisition/import/importTasks/taxonomyHelpers';
import { sendClientLog } from 'services/logService';

// Line 1 of the CMA headline (label sits beside this). Distinguishes the three
// result states: Failed (API error, red), NotFound (no CMA on file, neutral), Found ($ value).
const renderCmaValue = (cmaData, cmaStatus, cmaMessage, isFetchingCma, classes) => {
  if (isFetchingCma) {
    return (
      <div className={classes.cmaLoadingRow}>
        <CircularProgress size={16} />
      </div>
    );
  }
  // The API call itself failed (HTTP error, auth, network) — an error, not "no CMA".
  if (cmaStatus === 'Failed') {
    return (
      <Tooltip title={cmaMessage || 'CMA could not be retrieved. See logs for details.'} placement="bottom">
        <Typography variant="caption" className={classes.cmaError} noWrap>Unavailable — error</Typography>
      </Tooltip>
    );
  }
  // The call succeeded but the asset genuinely has no CMA on file — a plain, neutral label.
  if (cmaStatus === 'NotFound') {
    return <Typography variant="caption" className={classes.cmaUnavailable} noWrap>- None on file</Typography>;
  }
  const summary = cmaData?.summary;
  if (!summary) {
    return <Typography variant="caption" className={classes.cmaUnavailable} noWrap>- None on file</Typography>;
  }
  if (summary.kind === 'cma') {
    const value = summary.brokerCmaValue != null ? toCurrency(summary.brokerCmaValue, 0) : '—';
    return <Typography variant="h6" className={classes.cmaValue} noWrap>{value}</Typography>;
  }
  // feedback → value line stays empty; the flavor line carries the feedback text
  return null;
};

// Line 2 of the CMA headline. Only present for real records (cma/feedback); the
// NotFound/Failed states have no summary and render nothing here.
const renderCmaFlavor = (cmaData, classes) => {
  const summary = cmaData?.summary;
  if (!summary) return null;
  if (summary.kind === 'cma') {
    const flavor = [summary.type, summary.cmaType].filter(Boolean).join(' · ');
    return (
      <>
        {flavor && <Chip size="small" label={flavor} className={classes.cmaChip} />}
        {!summary.isComplete && <span className={classes.cmaBadge}>Preliminary</span>}
      </>
    );
  }
  // feedback
  return (
    <Chip
      size="small"
      label={summary.asOf ? `Feedback — ${asDate(summary.asOf)}` : 'Feedback'}
      className={classes.feedbackChip}
    />
  );
};

// Right side of line 2: completed date + listing agent. These live on the raw
// record, not the curated summary, so look up the summary's record in cmas[].
const renderCmaMeta = (cmaData, classes) => {
  const summary = cmaData?.summary;
  if (!summary || summary.kind !== 'cma') return null;
  const record = cmaData.cmas?.find(c => c.Id === summary.cmaId);
  if (!record) return null;
  const parts = [];
  if (record.DateCompleted__c) parts.push(asDate(record.DateCompleted__c));
  const agent = record.Listing_Agent_Name ?? record.Listing_Agent__c;
  if (agent) parts.push(agent);
  if (parts.length === 0) return null;
  return (
    <Typography variant="caption" className={classes.cmaMeta} noWrap>
      {parts.join(' · ')}
    </Typography>
  );
};

const PostAcqUWHeader = ({ containerClass, classes, push, selectedOpportunity, marketData, setPostAcqWorkflowGridStateUpdate,
  valuationIsDirty, fetchPostAcqUWMsrHomes, setIsFetchingPostAcqUWMsrHomes, isAmherstManaged, selectedContainer, viewingReadonlyMode,
  cmaData, cmaStatus, cmaMessage, isFetchingCma, setPostAcqCmaDialogIsOpen, fetchPostAcqCma, taxonomy,
  userHasUnderwriteManagerRole
}) => {
  const [isMsrHomesModalOpen, setIsMsrHomesModalOpen] = useState(false);
  const { openSaveUWDialog } = useSaveUWDialog();

  if (!selectedOpportunity) return null;

  if (!selectedContainer) return null;

  // CMA is not a feature for the Acquisitions channel — hide the affordance entirely.
  const isAcquisitions = isAcquisitionsChannel(taxonomy?.entries, selectedOpportunity.channel);
  // CMA is also hidden from junior underwriters — only Underwrite Managers see it.
  const showCma = !isAcquisitions && userHasUnderwriteManagerRole;

  const viewTaskOnWorkflow = id => {
    if (valuationIsDirty) {
      openSaveUWDialog({
        saveUWDialogParameters: { route: Constants.Routes.POSTACQ_WORKFLOW },
        callback: () => {
          setPostAcqWorkflowGridStateUpdate({ filterValue: [['id', '=', id]] }, true);
        }
      });
    } else {
      setPostAcqWorkflowGridStateUpdate({ filterValue: [['id', '=', id]] }, true); // Filter grid by selectedOpportunity id
      push(Constants.Routes.POSTACQ_WORKFLOW);
    }
  }

  return (
    <Paper className={classNames(containerClass, classes.root)} elevation={0}>
      <div className={classes.mainInfoContainer}>
        <HeaderImage
          viewTaskOnWorkflow={viewTaskOnWorkflow}
          viewingReadonlyMode={viewingReadonlyMode}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        <div className={classNames(classes.infoSection, classes.addressSection)} style={{ padding: 0 }}>
          <div className={classes.addressSectionRow}>
            {isAmherstManaged &&
              <div style={{ display: 'flex', paddingBotton: '3px' }}>
                <Tooltip title={"Amherst managed property"} placement="top">
                  <img src={AmherstIcon} alt="Amherst managed property" className={classes.amherstIcon} />
                </Tooltip>
                <Typography variant="h6" noWrap>{fullAddress(selectedOpportunity)}</Typography>
              </div>
            }
            {!isAmherstManaged && <Typography variant="h6" noWrap>{fullAddress(selectedOpportunity)}</Typography>}
          </div>
          <div className={classNames(classes.addressSectionRow, viewingReadonlyMode && classes.grayBackground)}>
            <BadgeSection selectedOpportunity={selectedOpportunity} />
          </div>
        </div>
        <div className={classNames(classes.vertDividerContainer, viewingReadonlyMode && classes.grayBackground)}>
          <Divider className={classes.vertDivider} />
        </div>
        <div className={classes.infoOverflow}>
          <div className={classNames(classes.infoSection, classes.priceSection, viewingReadonlyMode && classes.grayBackground)}>
            <div className={classes.textStrip}>
              <PriceSection module={Constants.Module.POSTACQ} selectedContainer={selectedContainer}/>
            </div>
            <div className={classes.textStrip}>
              <AuxInfo
                amherstEstimatedAMI={marketData?.amherstEstimatedAMI}
                schoolScoreAverage={selectedContainer?.schoolScoreAverage}
                schoolDistrict={selectedContainer?.schoolDistrict}
                selectedContainer={selectedContainer}
              />
            </div>
          </div>
          <div className={classNames(classes.vertDividerContainer, viewingReadonlyMode && classes.grayBackground)}>
            <Divider className={classes.vertDivider} />
          </div>
          {showCma && <>
          <div className={classNames(classes.infoSection, classes.cmaSection, viewingReadonlyMode && classes.grayBackground)}>
            <div className={classes.cmaRow}>
              <Typography variant="caption" className={classes.cmaLabel}>CMA</Typography>
              {renderCmaValue(cmaData, cmaStatus, cmaMessage, isFetchingCma, classes)}
              <div style={{ flexGrow: 1 }}/>
              <Tooltip title="View CMA details" placement="bottom">
                <span>
                  <IconButton
                    className={classes.cmaIconButton}
                    aria-label="View CMA details"
                    disabled={isFetchingCma}
                    onClick={() => {
                      sendClientLog('Info', `Post-Acq UW - User opened CMA modal for task ${selectedOpportunity.id}`);
                      // Re-fetch on open: the server cache returns the same data if recent,
                      // or fetches fresh from Everest if the cached entry has expired.
                      fetchPostAcqCma(selectedOpportunity.id);
                      setPostAcqCmaDialogIsOpen(true);
                    }}
                  >
                    <AssessmentIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              {cmaData?.summary?.lightningUrl && (
                <Tooltip title="View in Salesforce" placement="bottom">
                  <IconButton
                    className={classes.cmaIconButton}
                    aria-label="View in Salesforce"
                    onClick={() => window.open(cmaData.summary.lightningUrl, '_blank', 'noopener,noreferrer')}
                  >
                    <OpenInNewIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div className={classes.cmaRow}>
              {renderCmaFlavor(cmaData, classes)}
              <div style={{ flexGrow: 1, minWidth: 8 }}/>
              {renderCmaMeta(cmaData, classes)}
            </div>
          </div>
          <div className={classNames(classes.vertDividerContainer, viewingReadonlyMode && classes.grayBackground)}>
            <Divider className={classes.vertDivider} />
          </div>
          </>}
          <div className={classes.buttonWrapper}>
            <div className={classes.wrapper}>
              <Tooltip title="View MSR Homes" placement="bottom">
                <span>
                  <IconButton className={classes.headerButton} aria-label="View MSR Homes" disabled={viewingReadonlyMode} onClick={() => {
                    setIsFetchingPostAcqUWMsrHomes(true);
                    fetchPostAcqUWMsrHomes(selectedOpportunity.id);
                    setIsMsrHomesModalOpen(true);
                  }}>
                    <HouseIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
          <div className={classes.buttonWrapper}>
            <div className={classes.wrapper}>
              <Tooltip title='FEMA Map' placement="bottom">
                <IconButton className={classes.headerButton} aria-label="FEMA Map"
                  onClick={() => {
                    if (!selectedOpportunity.longitude || !selectedOpportunity.latitude) {
                      return false;
                    }
                    window.open(`https://msc.fema.gov/portal/search?AddressQuery=${selectedOpportunity.longitude},${selectedOpportunity.latitude}`, '_blank');
                }}>
                  <WaveIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
        </div>
        <ViewContainersModal module={Constants.Module.POSTACQ} txnOppId={selectedOpportunity.id}/>
        <MsrHomesModal
          module={Constants.Module.POSTACQ}
          isOpen={isMsrHomesModalOpen}
          setIsOpen={setIsMsrHomesModalOpen}
        />
        {!viewingReadonlyMode && showCma && <PostAcqCmaDialog />}
      </div>
    </Paper>
  )
};

const controlWidth = 44;
const styles = theme => ({
  root: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 0,
    borderBottom: '1px solid #ddd'
  },
  vertDividerContainer: {
    padding: `${theme.spacing(1.5)}px 0`
  },
  vertDivider: {
    width: 1,
    height: '100%'
  },
  mainInfoContainer: {
    display: 'flex',
    height: '100%',
    overflowX: 'auto',
    // overflowY: 'hidden'
  },
  infoSection: {
    display: 'flex',
    height: '80px',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  infoOverflow: {
    flexGrow: 1,
    display: 'flex', // B-added,
    paddingRight: '10px',
  },
  addressSection: {
    minWidth: 440
  },
  priceSection: {
  },
  textStrip: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    height: 30
  },
  headerButton: {
    padding: 4,
    margin: 4
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  wrapper: {
    position: 'relative',
  },
  amherstIcon: {
    height: '25px',
    width: '25px',
    marginRight: '5px',
    marginTop: '3px',
    borderRadius: `20%`,
    boxShadow: `0px 0px 5px 0px rgba(255, 255, 255, 0.6)`
  },
  addressSectionRow: {
    padding: '0 16px',
    height: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  grayBackground: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff'
  },
  cmaSection: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minWidth: 140
  },
  cmaLabel: {
    lineHeight: 1,
    marginRight: 6
  },
  cmaRow: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch', // fill the section width so the flexGrow spacer can push the buttons right
    height: 26
  },
  cmaMeta: {
    color: theme.palette.text.secondary,
    lineHeight: 1
  },
  cmaValue: {
    lineHeight: 1.2
  },
  cmaFlavor: {
    lineHeight: 1
  },
  cmaBadge: {
    fontWeight: 400,
    fontSize: '0.75rem',
    color: theme.palette.warning?.main || '#ed6c02',
    marginLeft: 4
  },
  // Flavor chip — matches the chip styling used in the CMA popup.
  cmaChip: {
    height: 22,
    backgroundColor: '#e3f2fd'
  },
  feedbackChip: {
    height: 22,
    backgroundColor: '#fff3e0'
  },
  // NotFound: the asset simply has no CMA — neutral, not an error.
  cmaUnavailable: {
    color: theme.palette.text.primary,
    lineHeight: 1
  },
  // Failed: the API call errored — call it out in red.
  cmaError: {
    color: theme.palette.error?.main || '#d32f2f',
    fontStyle: 'italic',
    lineHeight: 1
  },
  cmaIconButton: {
    padding: 4,
    marginLeft: 4
  },
  cmaLoadingRow: {
    display: 'flex',
    alignItems: 'center'
  },
  cmaLoadingText: {
    marginLeft: 6,
    color: theme.palette.text.secondary
  }
});

const mapStateToProps = (state, ownProps) => {
  const uwState = ownProps.viewingReadonlyMode ? state.postAcqUWState.underwriteModalState : state.postAcqUWState;

  return {
    selectedOpportunity: uwState.postAcqUWClientTask,
    selectedContainer: uwState.selectedContainer,
    valuationIsDirty: state.postAcqUWState.valuationIsDirty,
    isAmherstManaged: uwState.selectedOpportunityAmherstIndicative?.isAmherstManaged,
    marketData: uwState.marketData,
    cmaData: state.postAcqUWState.cmaData,
    cmaStatus: state.postAcqUWState.cmaStatus,
    cmaMessage: state.postAcqUWState.cmaMessage,
    isFetchingCma: state.postAcqUWState.isFetchingCma,
    taxonomy: postAcquisitionTaxonomySelector(state),
    userHasUnderwriteManagerRole: userRoleUnderwriteManagerSelector(state)
  }
};

const mapDispatchToProps = {
  push,
  setPostAcqWorkflowGridStateUpdate,
  fetchPostAcqUWMsrHomes,
  setIsFetchingPostAcqUWMsrHomes,
  setPostAcqCmaDialogIsOpen,
  fetchPostAcqCma
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PostAcqUWHeader);
